/**
 * V2.0.79 — Stripe checkout + return-URL polling extracted from
 * Editor.jsx.
 *
 * Owns the two halves of the Stripe flow that used to live inline:
 *   1. `startCheckoutSession(packId)` — creates a Stripe checkout
 *      session for the current design/pack and redirects the browser
 *      to it. Sets `checkingOut=true` for a spinner on the caller's
 *      "Sign Off" button.
 *   2. Return-URL polling — a one-shot `useEffect` that fires when
 *      `?session_id=...&billing=success|cancel` lands on the page.
 *      Polls `/api/checkout/status` up to 8 times (16s total) until
 *      Stripe confirms `paid`, then invokes the caller's `onPaid`
 *      handler with the returned `professional_credits` (so the
 *      caller can top up its credits state and auto-run sign-off).
 *
 * The `billingProcessedRef` guard prevents the effect firing twice
 * during React StrictMode's dev double-invoke, which was previously
 * causing duplicate sign-off runs on a slow network.
 */
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { startCheckout, checkoutStatus } from "../lib/api";

const MAX_POLL_ATTEMPTS = 8;
const POLL_INTERVAL_MS = 2000;

export default function useStripeCheckoutPolling({
  designId,
  searchParams,
  setSearchParams,
  onPaid,
  onExpired,
  onFailure,
  onPollStart,
}) {
  const [checkingOut, setCheckingOut] = useState(false);
  const [pollingPayment, setPollingPayment] = useState(false);
  const billingProcessedRef = useRef(false);

  // Refs to always hold the latest callback references. The polling
  // effect fires once (empty dep array) but calls these many times
  // over ~16s — if the caller re-renders with a new closure we need
  // the freshest logic, not the one captured at t=0.
  const cbRef = useRef({ onPaid, onExpired, onFailure, onPollStart });
  cbRef.current = { onPaid, onExpired, onFailure, onPollStart };

  const startCheckoutSession = async (packId = "concept") => {
    setCheckingOut(true);
    try {
      const r = await startCheckout(designId, packId);
      window.location.href = r.data.url;
    } catch (e) {
      toast.error("Could not start checkout");
      setCheckingOut(false);
    }
  };

  useEffect(() => {
    if (billingProcessedRef.current) return;
    const sessionId = searchParams.get("session_id");
    const billing = searchParams.get("billing");
    if (!sessionId && billing !== "cancel") return;
    billingProcessedRef.current = true;

    if (billing === "cancel") {
      toast("Payment cancelled");
      const next = new URLSearchParams(searchParams);
      next.delete("billing");
      setSearchParams(next, { replace: true });
      return;
    }
    if (!sessionId) return;

    cbRef.current.onPollStart?.();
    setPollingPayment(true);
    let attempts = 0;

    const poll = async () => {
      attempts += 1;
      try {
        const r = await checkoutStatus(sessionId);
        if (r.data.payment_status === "paid") {
          setPollingPayment(false);
          const next = new URLSearchParams(searchParams);
          next.delete("session_id");
          next.delete("billing");
          setSearchParams(next, { replace: true });
          toast.success("Payment confirmed — generating your Blueprint 2D PDF");
          cbRef.current.onPaid?.(r.data);
          return;
        }
        if (r.data.status === "expired") {
          setPollingPayment(false);
          toast.error("Payment session expired — try again");
          cbRef.current.onExpired?.();
          return;
        }
        if (attempts >= MAX_POLL_ATTEMPTS) {
          setPollingPayment(false);
          toast.error("Could not verify payment. Please retry sign-off.");
          cbRef.current.onFailure?.();
          return;
        }
        setTimeout(poll, POLL_INTERVAL_MS);
      } catch (err) {
        console.debug("[useStripeCheckoutPolling] status check failed:", err);
        setPollingPayment(false);
        toast.error("Payment status check failed");
        cbRef.current.onFailure?.();
      }
    };
    poll();
  }, []);

  return { checkingOut, pollingPayment, startCheckoutSession };
}
